<template>
  <div
    :class="[
      'plr-visual-hint-widget',
      'backdrop-dark',
      'animated',
      visualHint?.position ?? qrCode?.position ?? 'right'
    ]"
  >
    <figure
      :class="
        width && height
          ? { 'hint-container': true, vertical: width < height }
          : { 'hint-container': true }
      "
    >
      <div class="hint-toolbar">
        <button
          @click="isPaused = true"
          class="btn-pause"
          :disabled="!(duration && !isPaused)"
        />
        <button
          v-if="visualHint?.url ?? qrCodeUrl"
          @click="handleCopyClick"
          :disabled="!isCopyButtonEnabled"
          class="ctrl-c"
        />
        <button @click="handleCloseClick" class="btn-close" />
      </div>
      <Spinner
        color="white"
        class="main-spinner"
        style="top: 50%; left: 50%; transform: translate(-50%, -50%); position: absolute; z-index: -1"
      />
      <template v-if="visualHint">
        <template v-for="v of [true, false]" :key="v">
          <img
            v-if="v === (visualHint.position === 'left')"
            ref="imgRef"
            :src="visualHint.url"
            :class="width && height ? { vertical: width < height } : {}"
            :style="width && height ? { aspectRatio: width / height } : {}"
          />
        </template>
      </template>
      <img
        v-else-if="qrCodeUrl"
        ref="imgRef"
        :src="qrCodeUrl"
        :class="width && height ? { vertical: width < height } : {}"
        :style="width && height ? { aspectRatio: width / height } : {}"
      />
      <figcaption v-if="false">Picture Caption</figcaption>

      <ProgressBar
        v-if="duration"
        :duration="duration"
        :is-paused="isPaused"
        @end="handleProgressEnd"
        height="4"
        color="#FF681D"
        backgroundColor="#FFFFFF99"
        :style="{ visibility: duration && !isPaused ? 'visible' : 'hidden' }"
      />
    </figure>
  </div>
</template>
<script lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";
import {
  VisualHintDTO,
  QrCodeDTO,
  RequestedAction
} from "@moderrotech/servers-communication-library/src/types";
import _ from "lodash";
import Spinner from "@/components/Spinner.vue";
import {
  copyImageToClipboard,
  requestClipboardWritePermission
} from "copy-image-clipboard";
import { showToastError, showToastSuccess } from "@/helpers/notification";
import { i18n } from "@/lang";
import ProgressBar from "@/components/ProgressBar.vue";
import { useRafFn } from "@vueuse/core";
import { getUaParserResult } from "@moderrotech/servers-communication-library/src/helpers/device";
import { OSName } from "ua-parser-js/src/enums/ua-parser-enums";
/* eslint-disable */
// @ts-ignore
import { useQRCode } from '@vueuse/integrations/useQRCode.cjs'
/* eslint-enable */

export default {
  components: {
    ProgressBar,
    Spinner
  },
  setup() {
    const store = useStore();

    const visualHint = computed(
        (): VisualHintDTO | null => store.state.session.visualHint
      ),
      qrCode = computed((): QrCodeDTO | null => store.state.session.qrCode),
      duration = computed(
        () => (visualHint.value ?? qrCode.value)?.duration ?? null
      );

    const qrCodeUrl = useQRCode(qrCode.value?.text ?? "test bla", {
      width: 500
    });

    const unset = () => {
      store.commit("session/UNSET_VISUAL_HINT");
      store.commit("session/UNSET_QR_CODE");
    };

    const requestedRemoteActions = computed(
      (): { action: RequestedAction; payload?: any }[] | null =>
        store.state.session.requestedActions
    );

    const isPaused = ref(false);

    watch(
      () =>
        !!_.find(
          requestedRemoteActions.value,
          v => v.action === RequestedAction.pauseQrCode
        ),
      v => {
        if (v && qrCode.value) {
          isPaused.value = true;
          store.commit(
            "session/UNSET_REQUESTED_ACTION",
            RequestedAction.pauseQrCode
          );
        }
      },
      { immediate: true }
    );

    const imgRef = ref<HTMLImageElement>();

    const width = ref<number | null>(null),
      height = ref<number | null>(null);

    useRafFn(() => {
      width.value = imgRef.value?.naturalWidth ?? null;
      height.value = imgRef.value?.naturalHeight ?? null;
    });

    const isCordova = !!store.state.config.config?.mobileAppData,
      isCopyButtonEnabled = ref<boolean>();

    onMounted(() => {
      if (isCordova || getUaParserResult()?.os.name !== OSName.IOS) {
        isCopyButtonEnabled.value = true;
        return;
      }
      requestClipboardWritePermission().then(
        hasPermission => (isCopyButtonEnabled.value = hasPermission)
      );
    });

    return {
      visualHint,
      qrCode,
      qrCodeUrl,
      duration,
      isPaused,
      imgRef,
      width,
      height,
      handleCopyClick: () =>
        copyImageToClipboard(visualHint.value?.url ?? qrCodeUrl.value)
          .then(() => showToastSuccess(i18n.global.t("call.success.imageCopy")))
          .catch((err: Error) => {
            console.error("Can not copy image: ", err);
            showToastError(i18n.global.t("call.error.imageCopy"));
          }),
      handleCloseClick: () => unset(),
      handleProgressEnd: () => unset(),
      isCopyButtonEnabled
    };
  }
};
</script>

<style lang="scss">
@import "../assets/styles/breakpoints.module";
@import "../assets/styles/visual-hint.scss";
@import "../assets/styles/kiosk-landscape-30inch/visual-hint.scss";
@import "../assets/styles/kiosk-portrait-30inch/visual-hint.scss";
</style>
